import { useEffect, useRef } from "react";
import { applyBranding, brandingKey, buildBranding, loadSavedBranding, saveBranding } from "../utils/appBranding.js";

/**
 * Keeps the installed-app identity (manifest name/icons, iOS title, favicon, page title)
 * in sync with the default company. Saved locally so the next launch is branded before login.
 * Pass the defaultCompany from the same useCompanies() instance that edits companies,
 * so logo/name changes apply immediately.
 */
export function useAppBranding(defaultCompany) {
  const appliedKey = useRef(loadSavedBranding()?.key || null);

  useEffect(() => {
    if (!defaultCompany?.name) return undefined;
    let cancelled = false;

    const key = brandingKey(defaultCompany);
    const saved = loadSavedBranding();
    if (saved?.key === key) {
      // Same branding as cached: just make sure it's applied this session
      if (appliedKey.current !== `session:${key}`) {
        applyBranding(saved);
        appliedKey.current = `session:${key}`;
      }
      return undefined;
    }

    buildBranding(defaultCompany).then((branding) => {
      if (cancelled || !branding) return;
      applyBranding(branding);
      saveBranding(branding);
      appliedKey.current = `session:${branding.key}`;
    });

    return () => {
      cancelled = true;
    };
  }, [defaultCompany?.id, defaultCompany?.name, defaultCompany?.tradeName, defaultCompany?.appShortName, defaultCompany?.logoUrl]);

  return defaultCompany;
}

export default useAppBranding;
